/** Published policy text, as data rather than markup.
 *
 * One structure per document, rendered by the single generic `PolicyPage` component. Three
 * reasons it is shaped this way rather than as three hand-written JSX pages:
 *
 *  1. Hindi. Principle #9 forbids mixed-language screens, and a Hindi-speaking shopkeeper cannot
 *     meaningfully consent to an English-only privacy notice. Each block carries an optional `hi`
 *     twin and falls back to English the same way `localized()` does everywhere else.
 *  2. These documents are quoted verbatim in the apps and in the Play Console listing. Text in
 *     one place cannot drift out of sync with itself.
 *  3. Adding a fourth policy later is a data entry, not a new page (principle #3).
 *
 * PLACEHOLDERS: every [BRACKETED] value must be filled before these pages go live. They are
 * deliberately loud so a half-configured policy is obvious rather than plausible.
 *
 * The `hi` translations are intentionally absent. Machine-translated legal text is worse than
 * none — commission a human translator, then fill the `hi` keys.
 *
 * LAUNCH BLOCKER: the above is not a someday-nicety. Every page this file renders is English-only
 * right now, which fails a Hindi-speaking user exactly where consent matters most (principle #9,
 * "no mixed-language screens"). Do not ship to the Play Store until a human-translated `hi` twin
 * exists for every block below.
 */

// A fact the business owner has not supplied yet. Deliberately loud, same reasoning as the
// [BRACKETED] convention above — do not invent a value here (a wrong postal address or phone
// number in a legal notice is worse than an obviously-missing one). Every call site is listed in
// the launch-readiness report; confirm the real value with the owner before replacing it.
const CONTACT = {
  entity: "Swatah Software",
  proprietor: "Swarnima Verma",
  address: "Thana Tehsil Biswan, Biswan, Sitapur, Uttar Pradesh 261201",
  // Sole proprietorship: the proprietor IS the grievance officer. This held the entity name
  // while `proprietor` was an unfilled placeholder; with the real name known it rendered
  // "Swatah Software, Grievance Officer, Swatah Software", and the IT Act rules expect a
  // named individual rather than the firm restating itself.
  grievanceOfficer: "Swarnima Verma",
  // The public contact and grievance-officer email, one address for the whole estate (registry
  // v1.4). NEVER a swatahsoftware.com address — that is the proprietorship, not the company.
  grievanceEmail: "support@swatah.io",
  supportEmail: "support@swatah.io",
  supportPhone: "+91 92118 79911",
  jurisdiction: "Sitapur",
  effective: "24 August 2026",
};

const p = (en) => ({ type: "p", en });
const ul = (en) => ({ type: "ul", en });
const note = (en) => ({ type: "note", en });

export const LEGAL_DOCS = {
  privacy: {
    title: { en: "Privacy Notice" },
    intro: {
      en: `This notice explains what personal data Apna Shahar collects, why, and what you can do about it. It is written to stand on its own — you do not need to read our Terms of Service to understand it.`,
    },
    sections: [
      {
        h: { en: "Who we are" },
        blocks: [
          p(
            `Apna Shahar is operated by ${CONTACT.proprietor}, proprietor, trading as ${CONTACT.entity}, at ${CONTACT.address}, India. In this notice, "we" and "us" mean ${CONTACT.proprietor}, trading as ${CONTACT.entity}, and "you" means the person using Apna Shahar.`
          ),
        ],
      },
      {
        h: { en: "What we do not do" },
        blocks: [
          p(`These are worth stating first, because they are unusual.`),
          ul([
            "We do not sell your personal data to anyone.",
            "We do not use artificial intelligence. No AI models, no automated profiling, no algorithmic scoring of you or your business. Everything Apna Shahar shows you is produced by ordinary rules and database queries you could check by hand.",
            "We do not run third-party advertising trackers or analytics SDKs in our apps or on our website.",
            "We never see your payment card or bank details. We do not process payments between you and a business.",
            "We do not record or upload your voice. Voice search runs entirely on your own device.",
          ]),
        ],
      },
      {
        h: { en: "What we collect, and why" },
        blocks: [
          ul([
            "Mobile number — to create your account and sign you in by one-time password. This is the only mandatory item.",
            "Name — so businesses know who placed an order or booking.",
            "Email address — optional, for account recovery and receipts.",
            "Google account identifier — only if you choose to sign in with Google.",
            "Profile photo — optional, only if you upload one.",
            "Confirmation that you are 18 or over. We do not ask your age or date of birth, and we do not store either.",
            "Your city — to show you the right city's businesses.",
            "Approximate location — optional, only when you tap to use it, to sort businesses by distance. It is converted to a place name on your own device; we do not send your coordinates to any mapping company.",
            "Saved addresses — so you do not retype your address for every delivery.",
            "Orders, appointments, bookings, table reservations, task requests and quotes — to provide the service and show you your own history.",
            "Ambulance booking records — to dispatch help and keep an audit trail.",
            "Reviews, ratings, community posts, comments and likes — these are public by design, and your name appears with them.",
            "Favourites — to show you your saved list.",
            "Payment records — the amount, the date, the transaction reference you enter, and whether the business confirmed receipt. Not your bank or card details.",
            "Reward points — to operate the points and referral scheme.",
            "Notification tokens — to send you order and booking updates. Removed when you sign out.",
            "App version and device type — to fix problems.",
            "Photos you choose to upload.",
          ]),
          p(
            `If you own a business on Apna Shahar, we also hold your business name, address, category, opening hours, catalog, photos, your UPI ID (so customers can pay you directly), and your plan and billing records.`
          ),
        ],
      },
      {
        h: { en: "Employee data — an important distinction" },
        blocks: [
          p(
            `If a business owner uses Apna Shahar to manage their staff — attendance, salary, ratings — then that business owner decides what to collect and why. They are legally responsible for that data, and we act only on their instructions. In the language of the Digital Personal Data Protection Act, the business owner is the Data Fiduciary and we are their Data Processor.`
          ),
          p(
            `If you are an employee and want to know what your employer holds about you, or want it corrected or deleted, ask your employer. We will help them respond, but we cannot act without them, because it is not our data to give away.`
          ),
        ],
      },
      {
        h: { en: "Who we share data with" },
        blocks: [
          p(
            `With the business you transact with, we share your name, mobile number and the details of your order or booking — they cannot deliver your food or attend your appointment otherwise. For a broadcast task request, your phone number is hidden from workers until you assign one of them the job.`
          ),
          p(
            `With the service providers who help us run the platform, who process data only for that purpose and are contractually bound not to use it for anything else: Google Cloud Platform — Cloud SQL (database), Cloud Run (servers), Cloud Storage (uploaded images) and Firebase Hosting (website delivery) — Cloudflare (DNS), Expo (app notifications), Google (sign-in, if you use it), and Fast2SMS (one-time passwords). We do not use a payment gateway or payment aggregator for anything — not customer payments, not business plan fees — so none is listed here.`
          ),
          p(
            `Some of these operate outside India, which means your data may be processed outside India. We will also disclose data where the law requires it — a court order, a lawful request from an authority, or to establish or defend a legal claim.`
          ),
        ],
      },
      {
        h: { en: "Public information" },
        blocks: [
          p(
            `Reviews, ratings, community posts and comments are public. Your name and profile photo appear next to them and can be read by anyone, including people who are not signed in, and by search engines. Deleting your account removes your name from them.`
          ),
          p(
            `Business listings — name, address, phone number, opening hours and catalog — are public. That is the purpose of a directory.`
          ),
        ],
      },
      {
        h: { en: "Payments" },
        blocks: [
          p(
            `We do not handle money between you and a business. When you pay a business through Apna Shahar, you pay them directly through your own UPI app, into their own UPI ID. The money never passes through us, and we never see your bank account or card details.`
          ),
          p(
            `We are not a payment aggregator or payment gateway, we do not hold a Reserve Bank of India authorisation to act as one, and we never collect, hold or settle a customer's money at any point, for any transaction on Apna Shahar.`
          ),
          p(
            `We keep a record: the amount, the date, the transaction reference you enter, and whether the business has confirmed receiving it. Confirmation is manual — a business marks a payment as received. We record who marked it and when, but we cannot independently verify that money moved. If a business disputes your payment, use "report an issue" and the city operator will look into it.`
          ),
        ],
      },
      {
        h: { en: "How long we keep data" },
        blocks: [
          ul([
            "Account details — until you delete your account, then erased within 30 days.",
            "Orders, bookings, payments and other transaction records — 8 years, because tax law requires it. This applies even after you delete your account, but the records are detached from your name where possible.",
            "Reviews and community posts — until you or we delete them. Your name is removed when you delete your account.",
            "Ambulance records — 8 years, given the nature of the service.",
            "Security and access logs — at least 1 year.",
            "Notification tokens — deleted when you sign out.",
          ]),
        ],
      },
      {
        h: { en: "Your rights" },
        blocks: [
          ul([
            "See what personal data we hold about you and who we have shared it with.",
            "Correct anything inaccurate, incomplete or out of date.",
            "Delete your account and your data.",
            "Withdraw consent at any time. Withdrawing is as easy as giving it.",
            "Nominate someone to exercise these rights on your behalf if you die or become incapable.",
            "Complain to us, and if we do not resolve it, to the Data Protection Board of India.",
          ]),
          p(
            `To exercise any of these, email ${CONTACT.grievanceEmail} or use the option in the app. We will respond within 30 days.`
          ),
        ],
      },
      {
        h: { en: "Children" },
        blocks: [
          p(
            `Apna Shahar is for people aged 18 and over. We ask you to confirm you are 18 or older, and we do not knowingly allow anyone under 18 to create an account.`
          ),
          p(
            `We do not track, profile or advertise to children in any way — and because we do not do behavioural advertising or profiling to anyone at all, this is a property of how the platform is built, not just a policy. If you believe a child has created an account, tell us at ${CONTACT.grievanceEmail} and we will remove it.`
          ),
        ],
      },
      {
        h: { en: "Security" },
        blocks: [
          p(
            `We protect your data with encryption in transit, access controls limiting who can reach it, logging and monitoring, and regular backups. Your one-time passwords are stored hashed, never in readable form, and expire quickly. We do not use passwords for ordinary user accounts, so there is no password of yours for anyone to steal.`
          ),
          p(
            `No system is perfectly secure. If a breach occurs that affects your data, we will tell you without delay — what happened, what data was involved, what we are doing about it, and what you should do — and we will report it to the Data Protection Board of India within 72 hours.`
          ),
        ],
      },
      {
        h: { en: "Grievance Officer" },
        blocks: [
          p(
            `Grievance Officer: ${CONTACT.grievanceOfficer}, proprietor, trading as ${CONTACT.entity}. Email ${CONTACT.grievanceEmail}. Address ${CONTACT.address}. Phone ${CONTACT.supportPhone}.`
          ),
          p(
            `We will acknowledge your complaint within 7 days and resolve it within 90 days at the outside, usually far sooner. If you are not satisfied, you may complain to the Data Protection Board of India.`
          ),
        ],
      },
      {
        h: { en: "Changes" },
        blocks: [
          p(
            `If we change this notice materially, we will tell you in the app and by notification at least 14 days before the change takes effect. The date at the top always shows the current version.`
          ),
        ],
      },
    ],
  },

  terms: {
    title: { en: "Terms of Service" },
    intro: {
      en: `These terms are an agreement between you and ${CONTACT.proprietor}, proprietor, trading as ${CONTACT.entity}, at ${CONTACT.address}, which operates Apna Shahar. By creating an account or using Apna Shahar, you accept them. If you do not accept them, do not use the service.`,
    },
    sections: [
      {
        h: { en: "What Apna Shahar is — and is not" },
        blocks: [
          p(
            `Apna Shahar is a platform that connects you with local businesses and workers in your city. You can find them, contact them, order from them, book them, and review them.`
          ),
          note(
            `We are not the seller. When you order food, book a hotel room, see a doctor or hire a worker through Apna Shahar, your contract is with that business or worker, not with us. They are responsible for the goods and services they provide — quality, safety, timeliness, price and refunds. We are responsible for the platform that introduced you.`
          ),
          p(
            `We do not verify every claim a business makes about itself. Where a listing shows a "verified" mark, it means a city operator has checked specific details; it is not a guarantee of quality.`
          ),
        ],
      },
      {
        h: { en: "Who may use Apna Shahar" },
        blocks: [
          p(
            `You must be 18 years or older, and you confirm this when you sign up. You must give accurate information and keep it current. You are responsible for what happens under your account. We sign you in using a one-time password sent to your mobile number, or through your Google account — so keep control of your phone and your number.`
          ),
        ],
      },
      {
        h: { en: "Payments" },
        blocks: [
          p(
            `Payment for goods and services is between you and the business. We do not collect, hold or transfer that money. Where you pay by UPI through Apna Shahar, you pay the business's own UPI ID directly from your own app.`
          ),
          p(
            `A business marks a payment as received manually. We record who marked it and when, but we cannot independently confirm that money moved. Keep your own transaction reference. If there is a dispute, raise it with the business first, then use "report an issue". We are not a party to the payment and cannot reverse it. Refunds are the business's responsibility, on their own terms.`
          ),
        ],
      },
      {
        h: { en: "Reviews, ratings and community posts" },
        blocks: [
          p(`You may post reviews, ratings, comments and posts about businesses and places in your city. When you do, these rules apply. You must not post:`),
          ul([
            "Anything false or misleading, or about a business you have not actually dealt with.",
            "Abuse, harassment, threats, or hate speech about any person or group.",
            "Obscene or sexually explicit material.",
            "Anyone's personal information — phone numbers, addresses, identity documents — whether yours or someone else's.",
            "Anything unlawful, defamatory, or infringing someone's copyright or trademark.",
            "Advertising, spam, or repeated posting of the same content.",
            "Anything intended to manipulate ratings, including reviews you were paid to write.",
          ]),
          p(
            `You keep ownership of what you post, and you give us permission to display it on Apna Shahar, including publicly on our website.`
          ),
          p(
            `Every review, post and comment can be reported from within the app, and you can block another user, which hides their content from you. Reports are reviewed by the city operator. We may remove content that breaks these rules and suspend accounts that break them repeatedly. Where we remove your content we will tell you why, and you may respond to ${CONTACT.supportEmail}.`
          ),
        ],
      },
      {
        h: { en: "If you list a business" },
        blocks: [
          ul([
            "Your listing must be accurate. Do not misrepresent what you sell, your prices, your qualifications or your licences.",
            "You must hold whatever licences and registrations the law requires for your trade.",
            "You are responsible for what you sell and for honouring what you offer.",
            "You must handle customer data you receive through Apna Shahar lawfully, and only to fulfil that customer's order. Do not use it for marketing without their consent.",
            "If you use the staff and salary features, you are responsible for your employees' data.",
            "Marking a payment as received is a statement you are making. Marking one received when you have not received it, or refusing to mark one you have, may end your listing.",
            "Paid plans renew as described at purchase. You may cancel at any time; the plan runs to the end of the paid period. We do not refund part-periods.",
          ]),
        ],
      },
      {
        h: { en: "Emergency and ambulance services" },
        blocks: [
          note(
            `Apna Shahar helps you contact an ambulance provider. It is not an emergency service, it does not guarantee that help arrives, and in an emergency you should call 108 directly. Read the Emergency Disclaimer in full before using this feature.`
          ),
        ],
      },
      {
        h: { en: "What we do not promise" },
        blocks: [
          p(
            `We provide Apna Shahar as it is. We work hard to keep it running but we do not promise it will always be available, error-free, or up to date. Listings may be inaccurate or out of date. Businesses may close, change their prices, or fail to honour a booking.`
          ),
        ],
      },
      {
        h: { en: "Our liability" },
        blocks: [
          p(`To the extent the law allows:`),
          ul([
            "We are not liable for the goods or services a business provides, or for their acts or omissions.",
            "We are not liable for indirect or consequential loss — lost profits, lost opportunity, or loss of data.",
            "Where we are liable, our total liability to you is limited to the greater of ₹5,000 or the amount you paid us in the twelve months before the claim.",
          ]),
          p(
            `Nothing here limits liability that cannot be limited under Indian law, including for death or personal injury caused by our negligence, or for fraud.`
          ),
        ],
      },
      {
        h: { en: "Suspension and closure" },
        blocks: [
          p(
            `You may close your account at any time. We may suspend or close your account if you break these terms, if we are required to by law, or if your use harms other users or the platform. Where we can, we will tell you first and give you a chance to put it right. Serious cases — fraud, threats, repeated abuse — may be immediate.`
          ),
        ],
      },
      {
        h: { en: "Changes, law and disputes" },
        blocks: [
          p(
            `We may change these terms. Material changes will be notified in the app at least 14 days before they take effect. Continuing to use Apna Shahar after that means you accept them.`
          ),
          p(
            `These terms are governed by the law of India. Courts at ${CONTACT.jurisdiction}, Uttar Pradesh have exclusive jurisdiction. Before going to court, please contact us at ${CONTACT.supportEmail} — most disputes are resolved by a conversation.`
          ),
          p(
            `For complaints under the Information Technology Act and its rules, contact ${CONTACT.grievanceOfficer}, proprietor, trading as ${CONTACT.entity}, at ${CONTACT.grievanceEmail}, ${CONTACT.address}. We acknowledge complaints within 7 days.`
          ),
        ],
      },
    ],
  },

  emergency: {
    title: { en: "Ambulance and Emergency Disclaimer" },
    intro: {
      en: `Read this before using the ambulance feature.`,
    },
    sections: [
      {
        h: { en: "In an emergency, call 108" },
        blocks: [
          note(
            `Apna Shahar is not an emergency service. If someone is seriously ill or injured, call 108 — the free national ambulance number — or go directly to the nearest hospital. Do this first. Do not wait for a response through this app.`
          ),
        ],
      },
      {
        h: { en: "What this feature actually does" },
        blocks: [
          p(
            `Apna Shahar passes your request to independent, privately operated ambulance providers listed in your city, and shows you their response. That is all it does.`
          ),
          ul([
            "We do not operate ambulances. We do not employ drivers or paramedics and we own no vehicles.",
            "We do not guarantee that any provider will respond, or how quickly, or that a vehicle is available.",
            "We cannot guarantee the app will work when you need it. Your phone may have no signal, your battery may be flat, our servers may be down, or the provider may not see your request.",
            "We do not verify the medical training of any provider's staff, or the equipment in their vehicles, beyond checking basic registration details.",
          ]),
          p(
            `Ambulance requests through Apna Shahar are free of charge to you — we take no commission and no payment for this feature, and we never will. Any fee for the journey itself is charged by the ambulance provider directly, on their own terms.`
          ),
        ],
      },
      {
        h: { en: "Not a medical device" },
        blocks: [
          note(
            `Apna Shahar is not a medical device. It does not diagnose, treat, cure or prevent any disease or medical condition. It provides no medical advice, triage or clinical guidance of any kind. Always consult a qualified healthcare professional about any medical concern. Nothing in this app is a substitute for professional medical care.`
          ),
        ],
      },
      {
        h: { en: "Your responsibility" },
        blocks: [
          ul([
            "You will call 108 first in a genuine emergency.",
            "You will not rely on Apna Shahar as your only means of getting help.",
            "The information you enter — location, condition, contact number — must be accurate, because a provider will act on it.",
            "Misusing this feature by making false requests is serious. It can divert an ambulance from someone who is dying. We will close accounts that do it and report them to the authorities.",
          ]),
        ],
      },
      {
        h: { en: "Liability" },
        blocks: [
          p(
            `To the extent Indian law allows, we are not liable for any death, injury, loss or damage arising from the use of, or the inability to use, this feature — including delayed response, no response, a provider's conduct or competence, or a failure of the app, your device or the network. Your contract for the ambulance journey is with the provider, not with us. This does not limit any liability that cannot be limited under Indian law.`
          ),
          p(
            `Questions about this disclaimer: ${CONTACT.supportEmail}, ${CONTACT.supportPhone}. In an emergency, do not email us. Call 108.`
          ),
        ],
      },
    ],
  },
};

export const LEGAL_EFFECTIVE_DATE = CONTACT.effective;
export const LEGAL_CONTACT = CONTACT;
